import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { TipoSimulacao } from "../../enums/simulation-type.enum";
import { CaqRequestService } from "../caq-request/caq-request.service";
import { LocalizacaoService } from "../localizacao/localizacao.service";
import { CaqEscolasRespostaDto } from "../../dtos/caq-escola-resumo-dto";
import { MemoriaDeCalculoRequestDto } from "../memoria-de-calculo/memoria-de-calculo.service";

@Injectable({ providedIn: 'root' })
export class EscolasCaqService {
    private apiUrl = `${environment.apiUrl}/escolas-caq`;

    constructor(
        private caqRequestService: CaqRequestService,
        private localizacaoService: LocalizacaoService
    ) { }

    // Busca escolas com dados do banco/cache (sem edição)
    buscarEscolas(): Promise<CaqEscolasRespostaDto> {
        const url = this.construirUrlEscolasCaq(false);
        return firstValueFrom(
            this.caqRequestService.get<CaqEscolasRespostaDto>(url)
        );
    }
    
    // Recalcula com novos parâmetros editados
    recarcularMemoriaCalculoEscolas(requisicao: MemoriaDeCalculoRequestDto): Promise<CaqEscolasRespostaDto> {
        const url = this.construirUrlEscolasCaq(true);
        return firstValueFrom(
            this.caqRequestService.post<CaqEscolasRespostaDto>(url, requisicao)
        );
    }

    buscarNacional(): Promise<CaqEscolasRespostaDto> {
        return firstValueFrom(this.caqRequestService.get<CaqEscolasRespostaDto>(`${this.apiUrl}/nacional`));
    }

    buscarPlanoEstado(codUf: number | string): Promise<CaqEscolasRespostaDto> {
        return firstValueFrom(this.caqRequestService.get<CaqEscolasRespostaDto>(`${this.apiUrl}/plano/estado/${codUf}`));
    }

    buscarRedeEstado(codUf: number | string): Promise<CaqEscolasRespostaDto> {
        return firstValueFrom(this.caqRequestService.get<CaqEscolasRespostaDto>(`${this.apiUrl}/rede/estado/${codUf}`));
    }

    buscarPlanoMunicipio(codMun: number | string): Promise<CaqEscolasRespostaDto> {
        return firstValueFrom(this.caqRequestService.get<CaqEscolasRespostaDto>(`${this.apiUrl}/plano/municipio/${codMun}`));
    }

    buscarRedeMunicipio(codMun: number | string): Promise<CaqEscolasRespostaDto> {
        return firstValueFrom(this.caqRequestService.get<CaqEscolasRespostaDto>(`${this.apiUrl}/rede/municipio/${codMun}`));
    }

    /**
     * Constrói URL para buscar ou recalcular escolas CAQ
     * @param recalcular Se true, adiciona '/recalcular' no path (para POST). Se false, retorna path simples (para GET)
     */
    private construirUrlEscolasCaq(recalcular: boolean = false): string {
        const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();
        const prefixo = recalcular ? '/recalcular' : '';

        if (!localizacao) {
            return `${this.apiUrl}${prefixo}/nacional`;
        }

        const tipo = localizacao.tipoSimulacaoSelecionado;

        if (tipo === TipoSimulacao.planejamentoNacional) {
            return `${this.apiUrl}${prefixo}/nacional`;
        }

        if (localizacao.cidadeSelecionada) {
            const codMun = localizacao.cidadeSelecionada.value;
            if (tipo === TipoSimulacao.planejamento) {
                return `${this.apiUrl}${prefixo}/plano/municipio/${codMun}`;
            }
            if (tipo === TipoSimulacao.planejamentoPorEsferaAdmMunicipal) {
                return `${this.apiUrl}${prefixo}/rede/municipio/${codMun}`;
            }
        }

        if (localizacao.estadoSelecionado) {
            const codUf = localizacao.estadoSelecionado.value;
            if (tipo === TipoSimulacao.planejamento) {
                return `${this.apiUrl}${prefixo}/plano/estado/${codUf}`;
            }
            if (tipo === TipoSimulacao.planejamentoPorEsferaAdmEstadual) {
                return `${this.apiUrl}${prefixo}/rede/estado/${codUf}`;
            }
        }

        throw new Error('Tipo de simulação ou localização inválida para Escolas CAQ');
    }
}